import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

export const LocaleContext = React.createContext<{
  locale: string
  setLocale(locale: string): void
  availableLocales: string[]
} | null>(null)

export function useLocale() {
  const locale = useContext(LocaleContext)
  if (locale === null)
    throw new Error(
      'No locale provider found. Wrap your app in a <UseTranslateProvider>.'
    )

  return locale
}

interface LocaleProviderProps {
  initialLocale: string
  availableLocales: string[]
  onLocaleChange?(locale: string): void
}

export const LocaleProvider: React.FC<
  PropsWithChildren<LocaleProviderProps>
> = ({ children, initialLocale, availableLocales, onLocaleChange }) => {
  const [localLocale, setLocalLocale] = useState(initialLocale)

  const handleSetLocale = useCallback<
    React.Dispatch<React.SetStateAction<string>>
  >(
    locale => {
      const newLocale =
        typeof locale === 'function' ? locale(localLocale) : locale

      setLocalLocale(newLocale)
      onLocaleChange?.(newLocale)
    },
    [localLocale, onLocaleChange]
  )

  const value = useMemo(
    () => ({
      locale: localLocale,
      setLocale: handleSetLocale,
      availableLocales,
    }),
    [localLocale, handleSetLocale, availableLocales]
  )

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  )
}
